import dayjs from "dayjs";

import { shortMonthAndYearWithDelimiter } from "@similarweb/pro-logic-components";

import { swSettings } from "app/common/services/swSettings";
import CoreTrendsBarCell from "pro-features/components/core cells/src/CoreTrendsBarCell/CoreTrendsBarCell";
import {
    DefaultCellRightAlign,
    LeadingSite,
    ProgressBarPercentCell,
    SearchKeywordCell,
} from "app/components/React/Table/cells";
import { ChangeStateCell } from "app/components/React/Table/cells/ChangeStateCell";
import { IntentCell } from "app/components/React/Table/cells/IntentCell";
import { KeywordDifficultyCell } from "app/components/React/Table/cells/KeywordDifficultyCell";
import DurationService, { BasicDurations } from "app/services/DurationService";
import { RowSelectionConsumer } from "app/components/React/Table/cells/RowSelection";
import { DefaultCellHeaderRightAlign } from "app/components/React/Table/headerCells";
import { SelectAllRowsHeaderCellConsumer } from "app/components/React/Table/headerCells/SelectAllRowsHeaderCell";
import type { ITrendsBarValue } from "pro-features/components/TrendsBar/src/TrendsBar";
import { NO_DATA_SIGN, THRESHOLD_FOR_KEYWORD_RESEARCH_VALUE } from "app/filters/const";
import { translate } from "app/filters/i18nFilter";
import { minAbbrNumberFilter } from "app/filters/ngFilters";
import { EKWGeneratorTableTabs } from "app/common/keyword-research/keyword-generator/types";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import { devicesTypes } from "app/UtilitiesAndConstants/Constants/DevicesTypes";

const dateFormat = (date) => dayjs(date).format(shortMonthAndYearWithDelimiter);

export const getKeywordIdeasTableCoulmns = (
    country: string,
    duration: string,
    hasChange: boolean,
    isKeywordGroup: boolean,
    tab: EKWGeneratorTableTabs,
) => {
    const durationObject = DurationService.getDurationData(duration);
    const isWindowDuration = duration === BasicDurations.LAST_TWENTY_EIGHT_DAYS;
    const isWindow = durationObject.forAPI.isWindow;
    const showVolumeChange = isWindow && hasChange;

    return [
        isKeywordGroup && {
            fixed: true,
            cellComponent: RowSelectionConsumer,
            headerComponent: SelectAllRowsHeaderCellConsumer,
            isResizable: false,
            disableHeaderCellHover: true,
        },
        {
            field: "keyword",
            cellComponent: (props) => (
                <SearchKeywordCell
                    {...props}
                    destinationState={
                        swSettings.components.KeywordAnalysisV2Organic ? "organicsearch_keywordAnalysis_2" : undefined
                    }
                    adsUrl={proNavigator.href("paidsearch_keywordAnalysis_serp_ads", {
                        webSource: devicesTypes.TOTAL,
                        country,
                        duration,
                        keyword: props.value,
                    })}
                />
            ),
            displayName: translate("keyword.generator.tool.table.column.keyword"),
            tooltip: translate("keyword.generator.tool.table.column.keyword.tooltip"),
            width: tab === EKWGeneratorTableTabs.questions ? 258 : 180,
            cellClass: "keyword-cell",
            columnClass: "keyword-column",
            groupKey: "keywordGeneratorToolRelated",
        },
        isWindowDuration
            ? {
                  field: "windowVolume",
                  displayName: translate("website.keywords.2.0.table.window.volume.column"),
                  tooltip: translate("website.keywords.2.0.table.window.volume.column.tooltip"),
                  cellComponent: DefaultCellRightAlign,
                  headerComponent: DefaultCellHeaderRightAlign,
                  format: "minAbbrNumber:50",
                  minWidth: 90,
                  groupKey: "keywordGeneratorToolRelated",
              }
            : {
                  field: "monthlyVolume",
                  displayName: translate("keyword.generator.tool.table.table.column.volume"),
                  tooltip: translate("keyword.generator.tool.table.column.volume.tooltip"),
                  cellComponent: DefaultCellRightAlign,
                  headerComponent: DefaultCellHeaderRightAlign,
                  format: "minAbbrNumber:50",
                  minWidth: 90,
                  groupKey: "keywordGeneratorToolRelated",
              },
        showVolumeChange && {
            field: "VolumeChange",
            displayName: translate("website.keywords.2.0.table.window.volume.change.column"),
            tooltip: translate("website.keywords.2.0.table.volume.change.column.tooltip"),
            cellComponent: (props) => {
                const { change } = props.value;
                const absChange = Math.abs(change);
                const precision = absChange > 0 && absChange < 0.1 ? 1 : 0;
                return (
                    <ChangeStateCell
                        {...props}
                        undefinedSign={"-"}
                        isPercentage={true}
                        cellFilter={`simplePercentage:${precision}`}
                    />
                );
            },
            headerComponent: DefaultCellHeaderRightAlign,
            format: "percentagesign",
            width: 105,
        },
        {
            field: "volumeTrend",
            displayName: translate("keyword.generator.tool.table.column.volumetrend"),
            tooltip: translate("keyword.generator.tool.table.column.volumetrend.tooltip"),
            cellComponent: ({ value }) => {
                if (value) {
                    const data = Object.entries(value).map(([date, value]) => ({
                        value: value,
                        tooltip: (
                            <span>
                                <strong>{`${minAbbrNumberFilter()(
                                    value,
                                    THRESHOLD_FOR_KEYWORD_RESEARCH_VALUE,
                                )}`}</strong>
                                {` searches in ${dateFormat(date)}`}
                            </span>
                        ),
                    })) as ITrendsBarValue[];
                    return <CoreTrendsBarCell value={data} />;
                } else {
                    return NO_DATA_SIGN;
                }
            },
            format: "swPosition",
            width: 130,
            groupKey: "keywordGeneratorToolRelated",
        },
        {
            field: "intents",
            trackingName: "Keyword Intent",
            displayName: translate("keywords.table.header.intent.display.name"),
            tooltip: translate("keywords.table.header.intent.tooltip"),
            width: 120,
            cellComponent: IntentCell,
        },
        {
            field: "latestZeroClicks",
            displayName: translate("keyword.generator.tool.table.column.zero.click"),
            tooltip: translate("keyword.generator.tool.table.column.zero.clicks.tooltip"),
            cellComponent: (props) => <ProgressBarPercentCell value={props.value} />,
            width: 170,
        },
        {
            field: "difficulty",
            automationKey: "Difficulty",
            trackingName: "Keyword Difficulty",
            displayName: translate("analysis.source.search.all.table.columns.keyword.difficulty.title"),
            tooltip: translate("analysis.source.search.all.table.columns.keyword.difficulty.tooltip", {
                date: durationObject.raw.to.format("MMM YYYY"),
            }),
            width: 68,
            cellComponent: KeywordDifficultyCell,
            groupKey: "keywordGeneratorTool",
        },
        {
            field: "cpc",
            displayName: translate("keyword.generator.tool.table.column.cpc"),
            tooltip: translate("keyword.generator.tool.table.column.cpc.tooltip"),
            cellComponent: DefaultCellRightAlign,
            headerComponent: DefaultCellHeaderRightAlign,
            format: "CPC",
            width: 86,
            groupKey: "keywordGeneratorToolRelated",
        },
        {
            width: 200,
            field: "leadingSite",
            displayName: translate("keyword.generator.tool.table.column.website"),
            tooltip: translate("keyword.generator.tool.table.column.website.tooltip"),
            cellComponent: LeadingSite,
            groupKey: "keywordGeneratorToolRelated",
        },
    ]
        .filter((column) => !!column)
        .map((col) => {
            return {
                ...col,
                sortable: false,
                visible: true,
                disableHeaderCellHover: true,
                isResizable: false,
            };
        });
};
